import { useState, useEffect } from 'react';
import { Board } from '@/types';
import { getAllBoards } from '@/services/board.service';
import { useAuth } from '@/contexts/AuthContext';
import { logPageView, logError } from '@/services/analytics.service';
import { BoardList } from '@/components/organisms/BoardList';
import { PageHeader } from '@/components/organisms/PageHeader';
import { SearchInput } from '@/components/molecules/SearchInput';

export const BrowseBoardPage = () => {
  const { user } = useAuth();

  const [boards, setBoards] = useState<Board[]>([]);
  const [filteredBoards, setFilteredBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    logPageView('Browse Boards Page');
  }, []);

  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        const allBoards = await getAllBoards(user?.uid);
        setBoards(allBoards);
        setFilteredBoards(allBoards);
      } catch (error) {
        console.error('Error fetching boards:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch boards';
        logError('Browse Boards Fetch Error', errorMessage, 'BrowseBoardPage');
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, [user]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBoards(boards);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = boards.filter(
        (board) =>
          board.name.toLowerCase().includes(query) ||
          board.ownerUsername.toLowerCase().includes(query)
      );
      setFilteredBoards(filtered);
    }
  }, [searchQuery, boards]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader title="Browse Boards" showBackButton />

      {/* Search and Filters */}
      <section className="border-b border-border bg-muted/50">
        <div className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 w-full md:max-w-md">
              <SearchInput
                placeholder="Search boards by name or owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredBoards.length} board
              {filteredBoards.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>
      </section>

      {/* Boards Section */}
      <section className="max-w-[1200px] mx-auto px-4 py-12">
        <BoardList boards={filteredBoards} loading={loading} />
      </section>
    </div>
  );
};
